import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Card, Typography, Grid, Button, Stack, Input, Select, Option, Chip } from "@mui/joy";
import { Save, RotateCcw, ClipboardCheck, PackageCheck, Clock } from "lucide-react";
import { debounce } from "lodash";
import moment from "moment";

import { useInspectionAttributes, useMeasurement } from "../../hooks/query";
import { useProject } from "../../hooks";

import { ShingleType } from "../Inspection/components/ShingleType";
import { ColorSelection } from "../Inspection/components/ColorSelection";
import { RoofPitch } from "../Inspection/components/RoofPitch";
import { TearOff } from "../Inspection/components/TearOff";
import { UnderlaymentType } from "../Inspection/components/UnderlaymentType";
import { StarterType } from "../Inspection/components/StarterType";
import { RidgeCapProfile } from "../Inspection/components/RidgeCapProfile";
import { ChimneyType } from "../Inspection/components/ChimneyType";
import { ChimneySize } from "../Inspection/components/ChimneySize";
import { ChimneyColor } from "../Inspection/components/ChimneyColor";
import { VentilationType } from "../Inspection/components/VentilationType";
import { VentilationPainted } from "../Inspection/components/VentilationPainted";
import { FurnaceVentJackSize } from "../Inspection/components/FurnaceVentJackSize";
import { FurnaceVentCapCollar } from "../Inspection/components/FurnaceVentCapCollar";
import { EaveDripEdge } from "../Inspection/components/EaveDripEdge";
import { RakeDripEdge } from "../Inspection/components/RakeDripEdge";
import { ValleyType } from "../Inspection/components/ValleyType";
import { SkylightType } from "../Inspection/components/SkylightType";
import { SkylightSize } from "../Inspection/components/SkylightSize";
import { EvaporativeCooler } from "../Inspection/components/EvaporativeCooler";
import { GutterGuardType } from "../Inspection/components/GutterGuardType";
import { PropertyPhotos } from "../Inspection/components/PropertyPhotos";
import { InitialInspectionReport } from "../Inspection/components/InitialInspectionReport";
import { TaskActionComponent } from "../Tasks/components";

type ActionType = "R&R" | "Remove" | "Install" | "D&R";
const ACTION_OPTIONS: ActionType[] = ["R&R", "Remove", "Install", "D&R"];
const ACTION_COLORS: Record<ActionType, "primary" | "danger" | "success" | "warning"> = {
    "R&R": "primary", "Remove": "danger", "Install": "success", "D&R": "warning",
};

interface IInspectionV2 {
    activeTask: any;
    attributes: any[];
    setAttributes: (e: any) => void;
    measurements: any[];
    setMeasurements: (e: any) => void;
    onComplete: () => void;
}

export default function InspectionV2(props: IInspectionV2) {
    const { activeTask, attributes, setAttributes, measurements, setMeasurements, onComplete } = props;
    const { project } = useProject();
    const { useUpdateSingleAttribute } = useInspectionAttributes();
    const updateAttributeSvc = useUpdateSingleAttribute();
    const { useUpdateSingleMeasurement } = useMeasurement();
    const updateMeasurementSvc = useUpdateSingleMeasurement();

    // --- Visual measurement state ---
    const [visualData, setVisualData] = useState<any[]>([]);
    const [actions, setActions] = useState<Record<string, ActionType>>({
        "Roof Vent": "R&R", "Pipe Jack": "R&R", "Split Boots": "R&R",
        "Furnace Vents": "R&R", "Bathroom Vents": "R&R", "Dryer Vents": "R&R",
        "Chimney": "R&R", "Skylights": "R&R",
    });

    const visualItems = ["Roof Vent", "Pipe Jack", "Furnace Vents", "Bathroom Vents", "Dryer Vents", "Split Boots", "Chimney", "Skylights"];

    const processVisualData = useCallback(() => {
        if (!measurements || measurements.length === 0) return;
        const data = visualItems.map((name) => {
            const entry = measurements.find((m: any) => m.MeasurementType === name);
            if (!entry) return { name, actual: 0, id: null };
            return { name, actual: Number(entry.ActualValue), id: entry.Id };
        });
        setVisualData(data);
    }, [measurements]);

    useEffect(() => { processVisualData(); }, [processVisualData]);

    const debouncedMeasurementUpdate = useRef(
        debounce(async (recordId: string, newValue: number) => {
            try {
                await updateMeasurementSvc.mutateAsync({ Id: recordId, ActualValue: Number(newValue) });
                setMeasurements((prev: any[]) => prev.map((m) => (m.Id === recordId ? { ...m, ActualValue: Number(newValue) } : m)));
            } catch (error) { console.error(error); }
        }, 500),
    ).current;

    useEffect(() => () => { debouncedMeasurementUpdate.cancel(); }, [debouncedMeasurementUpdate]);

    const handleVisualInput = (itemName: string, value: number) => {
        setVisualData((prev) => prev.map((item) => {
            if (item.name === itemName) {
                if (item.id) debouncedMeasurementUpdate(item.id, value);
                return { ...item, actual: value };
            }
            return item;
        }));
    };

    const getVisualItem = (name: string) => visualData.find((v) => v.name === name);

    // --- Attribute helpers ---
    const getAttrValue = (name: string) => attributes?.find((attr: any) => attr.AttributeName === name)?.AttributeValue;

    const handleUpdate = async (value: string, attributeName: string) => {
        try {
            if (!project?.id) return;
            const attribute = attributes?.find((attr: any) => attr.AttributeName === attributeName);
            const attributeId = attribute?.Id;
            const updateParams: any = { Value: value };
            if (attributeId) { updateParams.Id = attributeId; }
            else { updateParams.ProjectId = project.id; updateParams.AttributeName = attributeName; }
            const response = await updateAttributeSvc.mutateAsync(updateParams);
            if (attributeId) {
                setAttributes((prev: any[]) => prev.map((attr: any) => attr.AttributeName === attributeName ? { ...attr, AttributeValue: value } : attr));
            } else {
                const newAttr = { Id: response?.Id || attribute?.Id, AttributeName: attributeName, AttributeValue: value };
                setAttributes((prev: any[]) => {
                    const exists = prev.some((a: any) => a.AttributeName === attributeName);
                    if (!exists) return [...prev, newAttr];
                    return prev.map((a: any) => a.AttributeName === attributeName ? { ...a, ...newAttr } : a);
                });
            }
        } catch (error) { console.error(`Error updating ${attributeName}:`, error); }
    };

    const sectionHeaderSx = { color: "text.tertiary", mb: 1.5, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "11px", fontWeight: "xl" };
    const cardSx = { p: 2, bgcolor: "background.surface", boxShadow: "none" };

    return (
        <Box>
            <Box mt={2} ml={1} mr={1}>
                {/* TASK HEADER — same as V1 */}
                <Typography startDecorator={<PackageCheck size={16} />} level="body-sm" gutterBottom>
                    Task
                </Typography>
                <Typography level="h4" sx={{ fontWeight: 500, fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
                    Initial Roof Inspection
                </Typography>

                <Box mt={2} display="flex" gap={1} flexWrap="wrap" alignItems="center">
                    <Chip
                        color={activeTask?.Status === "COMPLETED" ? "success" : "warning"}
                        sx={{ fontSize: 12, border: "1px solid", borderColor: "divider" }}
                    >
                        {activeTask?.Status}
                    </Chip>
                    <Chip color="neutral" variant="outlined" sx={{ fontSize: 12 }} startDecorator={<Clock size={14} />}>
                        Created On: {moment(activeTask?.CreatedAt).format("MM/DD/YYYY")}
                    </Chip>
                </Box>

                {/* DESCRIPTION */}
                <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                    <Typography level="body-sm" sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                        Description
                    </Typography>
                    <Typography level="body-md" sx={{ color: "text.primary" }}>
                        The primary goal of an Initial Roof Inspection is to assess the overall condition of a roof,
                        identify potential issues, document findings, and prepare a detailed report. This inspection
                        serves as a baseline evaluation for maintenance, repair, or replacement decisions.
                    </Typography>
                </Box>


                {/* PHOTOS — directly, no card wrapper */}
                <Box mt={2}>
                    <Typography level="body-sm" sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                        Photos
                    </Typography>
                    <Typography level="body-md" sx={{ color: "text.primary" }}>
                        Upload clear and detailed photos taken during the initial roof inspection to document its
                        current condition. These images will help assess any existing damages, structural concerns,
                        or areas requiring maintenance.
                    </Typography>
                    <Box mt={1}>
                        <PropertyPhotos taskDetails={activeTask} />
                    </Box>
                </Box>

                {/* WORKSHEET SECTIONS */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 4, mt: 4 }}>

            {/* GROUP: SHINGLE & ROOFING */}
            <Box>
                <Typography level="title-sm" sx={sectionHeaderSx}>Shingle & Roofing</Typography>
                <Card variant="outlined" sx={cardSx}>
                    <Grid container spacing={2}>
                        <Grid xs={12} sm={6} md={4}><ShingleType value={getAttrValue("Shingle Type")} onChange={(v) => handleUpdate(v, "Shingle Type")} /></Grid>
                        <Grid xs={12} sm={6} md={4}><ColorSelection value={getAttrValue("Roof Color")} onChange={(v) => handleUpdate(v, "Roof Color")} /></Grid>
                        <Grid xs={12} sm={6} md={4}><RoofPitch value={getAttrValue("Roof Pitch")} onChange={(v) => handleUpdate(v, "Roof Pitch")} /></Grid>
                        <Grid xs={12} sm={6} md={4}><TearOff value={getAttrValue("Tear Off Layer")} onChange={(v) => handleUpdate(v, "Tear Off Layer")} /></Grid>
                        <Grid xs={12} sm={6} md={4}><UnderlaymentType value={getAttrValue("Underlayment Type")} onChange={(v) => handleUpdate(v, "Underlayment Type")} /></Grid>
                        <Grid xs={12} sm={6} md={4}><StarterType value={getAttrValue("Starter Type")} onChange={(v) => handleUpdate(v, "Starter Type")} /></Grid>
                        <Grid xs={12} sm={6} md={4}><RidgeCapProfile value={getAttrValue("Ridge Cap Profile")} onChange={(v) => handleUpdate(v, "Ridge Cap Profile")} /></Grid>
                    </Grid>
                </Card>
            </Box>

            {/* GROUP: VENTILATION */}
            <Box>
                <Typography level="title-sm" sx={sectionHeaderSx}>Ventilation</Typography>
                <Card variant="outlined" sx={cardSx}>
                    <Grid container spacing={2}>
                        <Grid xs={12} sm={6} md={3}><VentilationType value={getAttrValue("Ventilation Type")} onChange={(v) => handleUpdate(v, "Ventilation Type")} /></Grid>
                        <Grid xs={12} sm={6} md={3}><VentilationPainted value={getAttrValue("Ventilation Painted")} onChange={(v) => handleUpdate(v, "Ventilation Painted")} /></Grid>
                        <Grid xs={12} sm={6} md={3}><FurnaceVentJackSize value={getAttrValue("Furnace Vent Jack Size")} onChange={(v) => handleUpdate(v, "Furnace Vent Jack Size")} /></Grid>
                        <Grid xs={12} sm={6} md={3}><FurnaceVentCapCollar value={getAttrValue("Furnace Vent Cap and Collar")} onChange={(v) => handleUpdate(v, "Furnace Vent Cap and Collar")} /></Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {(["Roof Vent", "Furnace Vents", "Bathroom Vents", "Dryer Vents"] as const).map((name) => (
                            <Grid xs={12} sm={6} md={3} key={name}>
                                <CountWithAction label={name} item={getVisualItem(name)} action={actions[name]} onQtyChange={(v) => handleVisualInput(name, v)} onActionChange={(a) => setActions((prev) => ({ ...prev, [name]: a }))} />
                            </Grid>
                        ))}
                    </Grid>
                </Card>
            </Box>

            {/* GROUP: PENETRATIONS */}
            <Box>
                <Typography level="title-sm" sx={sectionHeaderSx}>Penetrations</Typography>
                <Card variant="outlined" sx={cardSx}>
                    <Grid container spacing={2}>
                        {(["Pipe Jack", "Split Boots"] as const).map((name) => (
                            <Grid xs={12} sm={6} md={3} key={name}>
                                <CountWithAction label={name} item={getVisualItem(name)} action={actions[name]} onQtyChange={(v) => handleVisualInput(name, v)} onActionChange={(a) => setActions((prev) => ({ ...prev, [name]: a }))} />
                            </Grid>
                        ))}
                    </Grid>
                </Card>
            </Box>

            {/* GROUP: DRIP EDGE & VALLEY */}
            <Box>
                <Typography level="title-sm" sx={sectionHeaderSx}>Drip Edge & Valley</Typography>
                <Card variant="outlined" sx={cardSx}>
                    <Grid container spacing={2}>
                        <Grid xs={12} sm={6} md={4}><EaveDripEdge value={getAttrValue("Eave Drip Edge")} onChange={(v) => handleUpdate(v, "Eave Drip Edge")} /></Grid>
                        <Grid xs={12} sm={6} md={4}><RakeDripEdge value={getAttrValue("Rake Drip Edge")} onChange={(v) => handleUpdate(v, "Rake Drip Edge")} /></Grid>
                        <Grid xs={12} sm={6} md={4}><ValleyType value={getAttrValue("Valley Type")} onChange={(v) => handleUpdate(v, "Valley Type")} /></Grid>
                    </Grid>
                </Card>
            </Box>

            {/* GROUP: CHIMNEY */}
            <Box>
                <Typography level="title-sm" sx={sectionHeaderSx}>Chimney</Typography>
                <Card variant="outlined" sx={cardSx}>
                    <Grid container spacing={2}>
                        <Grid xs={12} sm={6} md={3}><ChimneyType value={getAttrValue("Chimney Type")} onChange={(v) => handleUpdate(v, "Chimney Type")} /></Grid>
                        <Grid xs={12} sm={6} md={3}><ChimneySize value={getAttrValue("Chimney Size")} onChange={(v) => handleUpdate(v, "Chimney Size")} /></Grid>
                        <Grid xs={12} sm={6} md={3}><ChimneyColor value={getAttrValue("Chimney Color")} onChange={(v) => handleUpdate(v, "Chimney Color")} /></Grid>
                        <Grid xs={12} sm={6} md={3}>
                            <CountWithAction label="Count" item={getVisualItem("Chimney")} action={actions["Chimney"]} onQtyChange={(v) => handleVisualInput("Chimney", v)} onActionChange={(a) => setActions((prev) => ({ ...prev, Chimney: a }))} />
                        </Grid>
                    </Grid>
                </Card>
            </Box>

            {/* GROUP: SKYLIGHT */}
            <Box>
                <Typography level="title-sm" sx={sectionHeaderSx}>Skylight</Typography>
                <Card variant="outlined" sx={cardSx}>
                    <Grid container spacing={2}>
                        <Grid xs={12} sm={6} md={4}><SkylightType value={getAttrValue("Skylight Type")} onChange={(v) => handleUpdate(v, "Skylight Type")} /></Grid>
                        <Grid xs={12} sm={6} md={4}><SkylightSize value={getAttrValue("Skylight Size")} onChange={(v) => handleUpdate(v, "Skylight Size")} /></Grid>
                        <Grid xs={12} sm={6} md={4}>
                            <CountWithAction label="Count" item={getVisualItem("Skylights")} action={actions["Skylights"]} onQtyChange={(v) => handleVisualInput("Skylights", v)} onActionChange={(a) => setActions((prev) => ({ ...prev, Skylights: a }))} />
                        </Grid>
                    </Grid>
                </Card>
            </Box>

            {/* GROUP: EQUIPMENT & GUTTERS */}
            <Box>
                <Typography level="title-sm" sx={sectionHeaderSx}>Equipment & Gutters</Typography>
                <Card variant="outlined" sx={cardSx}>
                    <Grid container spacing={2}>
                        <Grid xs={12} sm={6}><EvaporativeCooler value={getAttrValue("Has Evaporative Cooler")} onChange={(v) => handleUpdate(v, "Has Evaporative Cooler")} /></Grid>
                        <Grid xs={12} sm={6}><GutterGuardType value={getAttrValue("Gutter Guard Type")} onChange={(v) => handleUpdate(v, "Gutter Guard Type")} /></Grid>
                    </Grid>
                </Card>
            </Box>

                </Box>

                {/* DOCUMENTS */}
                <InitialInspectionReport />

                {/* TASK ACTION — complete task */}
                <TaskActionComponent taskName="Initial Inspection" onComplete={onComplete} isNextEligible={true} />
            </Box>
        </Box>
    );
}

/** Count input with an action override chip — visually matches Select/Input fields */
function CountWithAction({ label, item, action, onQtyChange, onActionChange }: {
    label: string; item: any; action: ActionType;
    onQtyChange: (v: number) => void; onActionChange: (a: ActionType) => void;
}) {
    return (
        <Box flex={1}>
            <Typography level="body-sm" sx={{ fontWeight: "md", mb: 0.5 }}>{label}</Typography>
            <Stack direction="row" spacing={1}>
                <Input
                    size="sm"
                    type="number"
                    placeholder="0"
                    value={item?.actual || ""}
                    onChange={(e) => onQtyChange(Math.max(0, Number(e.target.value) || 0))}
                    slotProps={{ input: { min: 0 } }}
                    sx={{ flex: 1 }}
                />
                <Select
                    size="sm"
                    variant="soft"
                    value={action}
                    onChange={(_e, val) => val && onActionChange(val as ActionType)}
                    slotProps={{ listbox: { sx: { zIndex: 10001 } } }}
                    sx={{ minWidth: 70 }}
                    renderValue={(selected) => (
                        <Chip size="sm" variant="soft" color={ACTION_COLORS[selected?.value as ActionType] || "neutral"} sx={{ fontSize: "9px", fontWeight: "bold" }}>
                            {selected?.label}
                        </Chip>
                    )}
                >
                    {ACTION_OPTIONS.map((opt) => <Option key={opt} value={opt}>{opt}</Option>)}
                </Select>
            </Stack>
        </Box>
    );
}
